import { getProp } from "../ParsedSummary";
import { collaborationLevelDescriptions } from "./questionBreakdown";
import { teamworkLevelLabel } from "utils/teamworkLevel";
import { collaborationLevelWording } from "utils/collaborationWording";
import { SOLO_ACCURACY_TITLE, SOLO_ACCURACY_RULE } from "utils/soloAccuracy";

export const studentHighlights = (
  summary,
  sections,
  isShortSession = false,
  deterministic = {},
) => {
  // Check for studentHighlights at root level or inside topHeadlines wrapper
  const wrapper = getProp(summary, ['topHeadlines', 'top_headlines', 'headlines']);
  const highlights = getProp(wrapper, ['studentHighlights', 'student_highlights'])
    || getProp(summary, ['studentHighlights', 'student_highlights']);
  if (!highlights || !Array.isArray(highlights) || highlights.length === 0) return;

  sections.push({
    type: 'title-main',
    content: 'Student Highlights'
  });

  highlights.forEach((student, index) => {
    const studentName = getProp(student, ['studentName', 'student_name', 'name']);
    sections.push({
      type: 'title-hr',
      content: studentName || `Student ${index + 1}`
    });

    // For short sessions, only show the headline
    const mathHeadline = getProp(student, ['mathHeadline', 'math_headline']);
    if (mathHeadline && typeof mathHeadline === 'string') {
      sections.push({
        type: 'bullet',
        label: 'Headline:',
        content: mathHeadline
      });
    }

    // Skip other details for short sessions
    if (isShortSession) return;

    // The math level bullet becomes the Solo Time fraction itself, coloured by
    // the accuracy. Where no record exists the LLM's math level still shows.
    const studentId = getProp(student, ['student_id', 'id']);
    const soloPill =
      deterministic.soloAccuracy?.[String(studentName || '').toLowerCase()] ||
      (studentId ? deterministic.soloAccuracy?.[String(studentId).toLowerCase()] : undefined);
    const mathLevel = getProp(student, ['mathLevel', 'math_level']);
    if (soloPill) {
      if (soloPill.notApplicable || soloPill.not_applicable) {
        sections.push({
          type: 'bullet',
          label: SOLO_ACCURACY_TITLE,
          content:
            soloPill.notApplicableReason ||
            soloPill.not_applicable_reason ||
            'Not applicable',
        });
      } else {
        sections.push({
          type: 'bullet',
          // A title like "Headline:" or "Focus:", with only the numbers inside the
          // pill beside it.
          label: SOLO_ACCURACY_TITLE,
          pillText: soloPill.fraction,
          pill: soloPill.tone,
          tooltip: SOLO_ACCURACY_RULE,
        });
      }
    } else if (mathLevel && typeof mathLevel === 'string') {
      const startsWithMath = mathLevel.trimStart().toLowerCase().startsWith('math');
      sections.push({
        type: 'bullet',
        content: startsWithMath ? mathLevel : `Math ${mathLevel}`
      });
    }

    // v9 renamed this to `teamworkDynamic`; the older keys stay so a report
    // generated before the rename keeps its bullet.
    const storedTeamworkLevel = getProp(student, ['teamworkLevel', 'teamwork_level', 'teamworkDynamic']);
    if (storedTeamworkLevel && typeof storedTeamworkLevel === 'string') {
      const teamworkLevel = collaborationLevelWording(storedTeamworkLevel);
      // The bolded label is this student's backend collaboration score; the
      // LLM keeps only the sentence after it.
      const backendLevel = deterministic.studentCollabLevels?.[
        String(studentName || '').toLowerCase()
      ];
      const backendLabel = teamworkLevelLabel(backendLevel);

      // "Level 3 - Effective Collaboration" reads as a collaboration level once
      // the word is in front of it. v9 sends a bare sentence instead, which the
      // word turns into "Collaboration Occasionally shared answers…" — so only
      // text that names a level, and does not already name the skill, is
      // prefixed.
      const namesALevel = /Level\s+\d+/i.test(teamworkLevel);
      const namesTheSkill = /^\s*(teamwork|collaboration)/i.test(teamworkLevel);
      const normalizedTeamwork = namesALevel && !namesTheSkill
        ? `Collaboration ${teamworkLevel}`
        : teamworkLevel;
      const levelMatch = normalizedTeamwork.match(/^(.*?Level\s+\d+(?::\s*|\s*-\s*)[^:]+):\s*(.*)$/i);

      const levelNum = backendLabel
        ? String(backendLevel)
        : String(teamworkLevel).match(/\d+/)?.[0];
      const tooltip = levelNum ? collaborationLevelDescriptions[levelNum] : undefined;

      if (backendLabel) {
        sections.push({
          type: 'bullet',
          label: backendLabel,
          // The backend supplies the whole label, so the LLM's text follows it
          // as written. v9 sends a bare sentence, which must not be prefixed
          // with a word the label already carries.
          content: levelMatch ? levelMatch[2] : teamworkLevel,
          tooltip
        });
      } else if (levelMatch) {
        sections.push({
          type: 'bullet',
          label: levelMatch[1],
          content: levelMatch[2],
          tooltip
        });
      } else {
        sections.push({
          type: 'bullet',
          content: normalizedTeamwork,
          tooltip
        });
      }
    }

    const recommendedNextSteps = getProp(student, ['recommendedNextStep', 'recommended_next_step', 'nextStep']);
    if (recommendedNextSteps && typeof recommendedNextSteps === 'string') {
      sections.push({
        type: 'bullet',
        label: 'Recommended Next Steps:',
        content: recommendedNextSteps
      });
    }
  });
};
